/**
 * Component to display related content for a leaf node within a given set of nested standards.
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {
    PhosphorIcon,
    type PhosphorIconAsset,
} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@khan/i18n";

import type {StandardMappedContentItem} from "./types.ts";

import articleIcon from "./images/article-icon.svg";
import contentContainerIcon from "./images/content-container-icon.svg";
import khanmigoIcon from "./images/khanmigo-icon.svg";
import curationPageIcon from "@phosphor-icons/core/bold/article-bold.svg";
import courseIcon from "@phosphor-icons/core/bold/book-open-bold.svg";
import domainIcon from "@phosphor-icons/core/bold/cube-bold.svg";
import folderIcon from "@phosphor-icons/core/bold/folder-simple-bold.svg";
import lessonIcon from "@phosphor-icons/core/bold/list-bullets-bold.svg";
import exerciseIcon from "@phosphor-icons/core/bold/pencil-simple-bold.svg";
import videoIcon from "@phosphor-icons/core/bold/play-bold.svg";
import rocketshipIcon from "@phosphor-icons/core/bold/rocket-launch-bold.svg";
import starburstIcon from "@phosphor-icons/core/fill/sparkle-fill.svg";
import fivePointedStarIcon from "@phosphor-icons/core/fill/star-fill.svg";

type Props = {
    mappedContent: Array<StandardMappedContentItem>;
};

function iconForKind(kind: string): PhosphorIconAsset {
    switch (kind) {
        case "AIGuideActivity":
            return khanmigoIcon;
        case "Article":
            return articleIcon;
        case "Video":
            return videoIcon;
        case "Talkthrough":
        case "Interactive":
        case "Challenge":
            return fivePointedStarIcon;
        case "Exercise":
            return exerciseIcon;
        case "Project":
            return rocketshipIcon;
        case "Quiz":
        case "UnitTest":
            return starburstIcon;
        case "Course":
            return courseIcon;
        case "Unit":
            return contentContainerIcon;
        case "Lesson":
            return lessonIcon;
        case "Domain":
            return domainIcon;
        case "CurationPage":
            return curationPageIcon;
        case "Topic":
        default:
            return folderIcon;
    }
}

const StandardsTreeLeafRelatedContent = (props: Props): React.ReactElement => {
    const {mappedContent} = props;

    if (mappedContent.length === 0) {
        return (
            <View>
                <Body style={styles.unavailableText}>
                    {doNotTranslate`(Content unavailable)`}
                </Body>
            </View>
        );
    }

    return (
        <ul className={css(styles.list)}>
            {mappedContent.map((content, index) => {
                return (
                    <li
                        key={`${index}.${content.id}`}
                        className={css(styles.listItem)}
                    >
                        <Link href={content.urlWithinStandardSet}>
                            <View style={styles.relatedContentWrapper}>
                                {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                                <View alt={`${content.contentKind}`}>
                                    <View style={styles.contentIcon}>
                                        <PhosphorIcon
                                            icon={iconForKind(
                                                content.contentKind,
                                            )}
                                            size="small"
                                            color={color.offBlack}
                                        />
                                    </View>
                                </View>
                                <Body>{content.title}</Body>
                            </View>
                        </Link>
                    </li>
                );
            })}
        </ul>
    );
};

const styles = StyleSheet.create({
    list: {
        marginLeft: spacing.xxxLarge_64,
    },
    listItem: {
        display: "flex", // Without this, the entire horizontal line is a click target.
        marginBottom: spacing.small_12,
    },
    unavailableText: {
        color: color.offBlack64,
        marginLeft: spacing.xxxLarge_64,
    },
    relatedContentWrapper: {
        flexDirection: "row",
        alignItems: "left",
    },
    contentIcon: {
        marginRight: spacing.small_12,
        height: 24,
        width: 24,
        padding: 4,
        cursor: "pointer",
        outline: "none",
        background: "none",
        borderWidth: 1,
        borderColor: color.offBlack32,
        borderStyle: "solid",
        borderRadius: 4,
        color: color.offBlack,
        justifyContent: "center",
        alignItems: "center",
    },
});

export default StandardsTreeLeafRelatedContent;
